import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { UtilsService } from '@ebw-monorepo/utils';
import { optionsFromCapabilities } from 'ol/source/WMTS';
import { Subject } from 'rxjs';
import { IProjection } from '../interfaces/mapSettings.interface';
import { LayerStylesService } from './layer-styles.service';
import { MapService } from './map.service';
import { LoginService } from '@ebw-monorepo/login';
import { API_URLS, ApiUrlService } from '../../../../utils/src/lib/services/api-url.service';

/**
 * service to handle the dynamic layers form and its submission
 */
@Injectable({
  providedIn: 'root'
})
export class DynamicLayersFormService {
  // subject to notify the subscribers when the list of form gets updated
  formListUpdate$: Subject<Map<string, FormGroup>>;
  // subject to notify the WMTS form components when the WMTS options (availableTileMatrixSets, availableFormats) get updated
  wmtsOptionsUpdate$: Subject<Map<string, { availableTileMatrixSets: string[]; availableFormats: string[] }>>;

  // map of form objects, identified by an ID (string, Date.now())
  forms: Map<string, FormGroup>;
  // map of WMTS options for each form component
  wmtsOptions: Map<string, { availableTileMatrixSets: string[]; availableFormats: string[] }>;

  // variable to keep track of the current type of layers that are being added (WMS, WMTS, XYZ, Vector)
  _serviceType: string;
  // object to store the currently downloaded capabilities for the WMS/WMTS layers
  _capabilities: any;

  // list of objects representing the available layers in the current capabilities
  _capabilityLayers: any[];
  // list of registered projections in the map
  registeredProjections: IProjection[];
  // list of the available projections (calculated between capability projections and registered projections)
  availableProjections: IProjection[];
  // list of available style options loaded in the map
  styleOptions: string[];
  // list of default accepted image formats
  defaultFormats: string[];
  // list of accepted image formats for WMTS layers
  availableFormats: string[];
  // list of available tile matrix sets from WMTS capabilities (subset of capability tile matrix sets including only the ones in an available projection)
  availableTileMatrixSets: string[];

  /**
   * constructor method of the service
   * @param _mapService service used to access the open layers map
   * @param _layerStylesService service used to load the available styles in the map
   * @param _utilsService service used to make HTTP requests
   * @param _i18nService service used to translate text
   * @param _ebwModalService service used to display errors to the user
   */
  constructor(
    private _mapService: MapService,
    private _layerStylesService: LayerStylesService,
    private _utilsService: UtilsService,
    private _i18nService: I18NService,
    private _ebwModalService: EbwModalService,
    private _apiUrlService: ApiUrlService
  ) {
    // initialize all the fields of the service
    this.forms = new Map<string, FormGroup>();
    this.formListUpdate$ = new Subject<Map<string, FormGroup>>();

    this.wmtsOptions = new Map<string, { availableTileMatrixSets: string[]; availableFormats: string[] }>();
    this.wmtsOptionsUpdate$ = new Subject<Map<string, { availableTileMatrixSets: string[]; availableFormats: string[] }>>();

    this.registeredProjections = this._mapService.registeredProjections;
    this.styleOptions = this._layerStylesService.mapStyles.map((element) => {
      return element.name;
    });
    this.defaultFormats = ['image/png', 'image/jpeg', 'image/gif'];
  }

  /**
   * method to add a new form object to the map, this will be connected to a component
   * @method
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  addForm() {
    // calculate a new ID based on the current time
    const id: string = Date.now().toString();

    // create a new form group object with a 'title' controller
    this.forms.set(
      id,
      new FormGroup({
        title: new FormControl('', [Validators.required, this.layerNameValidator])
      })
    );

    // update the form group and setup the necessary subscriptions depending on the service type
    switch (this.serviceType) {
      // WMS layer
      case 'WMS':
        // add the necessary form controls
        this.forms.get(id).addControl('layer', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('format', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('crs', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('styles', new FormControl(''));
        this.forms.get(id).addControl('image', new FormControl(false));

        // setup a subscription to changes on the layer control
        this.forms.get(id).controls.layer.valueChanges.subscribe((choice) => {
          // stop in case no choice was made
          if (!choice || choice == '') {
            return;
          }

          // get the name of the service (to use as layer name in case none was already specified)
          const serviceTitle = this._capabilityLayers.find((el) => el.Name === choice).Title;
          // check that no title was given yet, and if that's the case, fill the title control with the service title
          if (!this.forms.get(id).controls.title.getRawValue() || this.forms.get(id).controls.title.getRawValue() == '') {
            // fill the title of the layer with the layer name stripped of all the problematic characters
            this.forms.get(id).controls.title.setValue(serviceTitle.replace(/[`!@#$%^&*()+\-=\[\]{};:"\\|,.<>\/?~]/g, ''));
          }
        });

        break;

      // WMTS layer
      case 'WMTS':
        // add the necessary form controls
        this.forms.get(id).addControl('layer', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('format', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('tilematrixset', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('styles', new FormControl(''));

        // disable the format and tilematrixset controls as they depend on the choice of the layer
        this.forms.get(id).controls.format.disable();
        this.forms.get(id).controls.tilematrixset.disable();

        // subscribe to changes on the layers control
        this.forms.get(id).controls.layer.valueChanges.subscribe((choice) => {
          // stop in case no choice was made
          if (!choice || choice == '') {
            return;
          }

          // get the name of the service (to use as layer name in case none was already specified)
          const serviceTitle = this._capabilityLayers.find((el) => el.Identifier === choice).Title;
          // check that no title was given yet, and if that's the case, fill the title control with the service title
          if (!this.forms.get(id).controls.title.getRawValue() || this.forms.get(id).controls.title.getRawValue() == '') {
            // fill the title of the layer with the layer name stripped of all the problematic characters
            this.forms.get(id).controls.title.setValue(serviceTitle.replace(/[`!@#$%^&*()+\-=\[\]{};:"\\|,.<>\/?~]/g, ''));
          }

          // get the capabilities of the currently selected layer
          const currentLayer = this._capabilityLayers.find((el) => el.Identifier == choice);

          // store the available formats given by the capabilities
          const availableFormats = [];
          for (const format of currentLayer.Format) {
            // parse the format to remove additional information provided
            availableFormats.push(format.split(';')[0]);
          }

          // store the available matrix sets given by the capabilities
          const availableTileMatrixSets = [];

          // iterate through all the matrices in the current layer
          for (const matrix of currentLayer.TileMatrixSetLink) {
            // try to create the layer options from the capabilities, if it fails, it means the EPSG code corresponding to the matrix set
            // isn't loaded in the map (optionsFromCapabilities detects the projection from the matrix automatically)
            try {
              optionsFromCapabilities(this.capabilities, {
                layer: choice.id,
                matrixSet: matrix.TileMatrixSet
              });

              availableTileMatrixSets.push(matrix.TileMatrixSet);
            } catch (error) {
              console.error('[DynamicLayersFormService][addForm][WMTS]', error);
            }
          }

          // set the value of the map of WMTS options with the extracted data for the correct ID
          this.wmtsOptions.set(id, {
            availableTileMatrixSets: availableTileMatrixSets,
            availableFormats: availableFormats
          });

          // notify the form components of the changes
          this.wmtsOptionsUpdate$.next(this.wmtsOptions);

          // enable the format and tilematrixset controls now that the necessary data has been retrieved
          this.forms.get(id).controls.format.enable();
          this.forms.get(id).controls.tilematrixset.enable();
        });

        break;

      // XYZ layer
      case 'XYZ':
        // add the necessary form controls
        this.forms.get(id).addControl('url', new FormControl('', [Validators.required]));

        break;

      // Vector layer
      case 'Vector':
        // add the necessary form controls
        this.forms.get(id).addControl('url', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('type', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('dataProjection', new FormControl('', [Validators.required]));
        this.forms.get(id).addControl('styles', new FormControl(''));
        break;

      // unforseen case
      default:
        console.log('error');
        break;
    }

    // update the components of the changes to the forms
    this.formListUpdate$.next(this.forms);
  }

  /**
   * method to delete a specified form and its relative WMTS options (in case they exist)
   * @param {string} id id of the form to remove
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  removeForm(id: string) {
    // delete the form
    this.forms.delete(id);
    // update the components of the changes
    this.formListUpdate$.next(this.forms);
    // delete the WMTS options
    this.wmtsOptions.delete(id);
    // update the components of the changes
    this.wmtsOptionsUpdate$.next(this.wmtsOptions);
  }

  /**
   * method to save the form data, upload it to the database through the backend and add the layers to the openlayers map
   * @param {FormGroup} outerForm outer form object to get its data (url in case of WMS / WMTS)
   * @returns {Promise<string>} close status, can be CORRECT, RELOAD or ERROR (or undefined in case of invalid forms)
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  async save(outerForm: FormGroup): Promise<string> {
    if (!this.checkValidForm(outerForm)) {
      return undefined;
    }

    // default status of the return of this form
    let closeStatus = 'RELOAD';

    // cycle through all the forms
    for (const form of this.forms.values()) {
      // build the param object from the values of the controls
      const params: any = this._getParam(form, outerForm);

      // setup the URL with the parameters that are common to all layers
      let url = this._apiUrlService.createUrl(API_URLS.MAP.LAYER.ADD_LAYER_THIRDPARTY);

      // make a post request to the load api
      await this._utilsService
        .postRequest(url, params, false, {}, 'application/json')
        .toPromise()
        .catch((error) => {
          // catch and display to the user any error in the process
          this._ebwModalService.openErrorModal({
            title: this._i18nService.getInstantValue('common.error'),
            content: this._i18nService.getInstantValue('common.error_saving')
          });
          closeStatus = 'ERROR';
        });
    }

    return closeStatus;
  }

  /**
   * method to clear the service of the stored data (forms and wmts options)
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  clear() {
    // create a new map for the forms
    this.forms = new Map<string, FormGroup>();
    // update the components of the changes
    this.formListUpdate$.next(this.forms);

    // create a new map for the WMTS options
    this.wmtsOptions = new Map<string, { availableTileMatrixSets: string[]; availableFormats: string[] }>();
    // update the components of the changes
    this.wmtsOptionsUpdate$.next(this.wmtsOptions);
  }

  /**
   * service type setter
   */
  set serviceType(value: string) {
    // check that it's setting a valid value and clear the forms in case it is valid
    if (value === 'WMS' || value === 'WMTS' || value === 'XYZ' || value === 'Vector') {
      this._serviceType = value;
      this.clear();
    }
  }

  /**
   * service type getter
   */
  get serviceType(): string {
    return this._serviceType;
  }

  /**
   * capability layers setter
   */
  set capabilityLayers(value: any[]) {
    this._capabilityLayers = value;
  }

  /**
   * capability layers getter (returns a copy of the array)
   */
  get capabilityLayers(): any[] {
    return this._capabilityLayers.slice();
  }

  /**
   * capabilities setter, calculates the necessary data from the capabilities
   */
  set capabilities(value: any) {
    // store the new value
    this._capabilities = value;

    // extract the necessary data depending on the service type
    switch (this.serviceType) {
      // WMS capabilities
      case 'WMS':
        if (this.capabilities && this.capabilities.Capability && this.capabilities.Capability.Layer && this.capabilities.Capability.Layer.Layer) {
          // get the default image formats
          this.availableFormats = this.defaultFormats;
          // store the available projections from the capabilities
          let capabilityProjections = this.capabilities.Capability.Layer.CRS;
          // calculate the intersection between the registered projections and the capability projections
          this.availableProjections = this.registeredProjections.filter((v) => capabilityProjections.includes(v.code));
          // store the layers obtained from the capabilities
          this._capabilityLayers = this.capabilities.Capability.Layer.Layer;
          // sort the layers by title to make it easier to find in the UI
          this._capabilityLayers.sort((a, b) => (a.Title > b.Title ? 1 : b.Title > a.Title ? -1 : 0));
        } else {
          this._ebwModalService.openErrorModal({
            content: this._i18nService.getInstantValue('common.data_retrieving_error'),
            title: this._i18nService.getInstantValue('common.error')
          });
        }

        break;

      // WMTS capabilities
      case 'WMTS':
        if (this.capabilities && this.capabilities.Contents && this.capabilities.Contents.Layer) {
          // store the layers obtained from the capabilities
          this._capabilityLayers = this.capabilities.Contents.Layer;
          // sort the layers by title to make it easier to find in the UI
          this._capabilityLayers.sort((a, b) => (a.Title > b.Title ? 1 : b.Title > a.Title ? -1 : 0));
        } else {
          this._ebwModalService.openErrorModal({
            content: this._i18nService.getInstantValue('common.data_retrieving_error'),
            title: this._i18nService.getInstantValue('common.error')
          });
        }

        break;

      default:
        break;
    }
  }

  /**
   * capabilities getter
   */
  get capabilities(): any {
    return this._capabilities;
  }

  /**
   * method to extract layer data from the form groups
   * @param {FormGroup} form layer specific form, contains information about the single layers
   * @param {FormGroup} outerForm service type wide form, contains the url and service type of the layer (in case of WMS/WMTS)
   * @returns {any} object contanining the information required for setting up a layer
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  private _getParam(form: FormGroup, outerForm: FormGroup): any {
    // object to store the information coming from the controls
    let title: string;
    // setup the object with a field 'params' which is also an object
    let params = {};

    // iterate through every control in the form
    for (const name in form.controls) {
      if (name === 'layer') {
        // check to make sure that if it's a WMS layer, the field is called 'layers' instead
        params[name + (this.serviceType === 'WMS' ? 's' : '')] = form.controls[name].value;
      } else if (name === 'crs') {
        // check to make sure that if it's a WMS layer, the 'crs' field is also stored as 'srs'
        params[name] = form.controls[name].value;
        params['srs'] = form.controls[name].value;
      } else if (name === 'title') {
        // store the layer title is stored at the top level of the object, and not inside the 'params' field
        title = form.controls[name].value;
      } else {
        // store the rest of the values inside params
        params[name] = form.controls[name].value;
      }
    }
    // if there is a value (e.g., EPSG:4326), split and take the numeric part
    if (!!params['srs']) {
      params['srs'] = params['srs'].split(':')[1];
    }

    let obj = { title, ...params };

    // extract the url from the outer form (in case of layer WMS or WMTS)
    if (this.serviceType === 'WMS' || this.serviceType === 'WMTS') {
      obj['url'] = outerForm.controls.url.value;
    }
    // otherwise extract it from the inner form
    else {
      obj['url'] = form.controls.url.value;
    }

    obj['visibility'] = {};
    // se privato metto la visibilità per l'utente
    // TODO gestire nel futuro casi di visibilità a gruppi (va modificata l'interfaccia per far scegliere i gruppi a cui l'utente appartiene)
    if (!outerForm.controls.visibility.value) {
      obj['visibility'] = { user_id: sessionStorage.getItem(LoginService.USER_ID_KEY) };
    }

    obj['auth'] = {
      method: 'GET',
      type: 'oauth',
      key: ''
    };

    // set the layergroup of the layer to 'THIRDPARTY'
    obj['layer_group'] = 'THIRDPARTY';
    obj['type'] = this._serviceType;

    // return the object filled with the control information
    return obj;
  }

  /**
   * custom validator to check that the layer title doesn't contain any invalid characters
   * @param {FormControl} control control object to check the validity of
   * @returns the status of the control, whether it's invalid or not, and how it's invalid
   * @author Joshua Micheletti <j.micheletti@ebw.it>
   */
  layerNameValidator(control: FormControl): { [s: string]: boolean } {
    let checkSpecialCharacters = /[`!@#$%^&*()+\-=\[\]{};:"\\|,.<>\/?~]/.test(control.getRawValue());

    if (checkSpecialCharacters) {
      return { invalid: true };
    }

    return null;
  }

  checkValidForm(outerForm: FormGroup) {
    let valid = true;

    // serviceType always required — blocks save when no type selected yet
    const serviceTypeCtrl = outerForm.controls['serviceType'];
    if (serviceTypeCtrl.invalid) {
      serviceTypeCtrl.markAsTouched();
      serviceTypeCtrl.markAsDirty();
      valid = false;
    }

    // outer url only used for WMS/WMTS (capabilities endpoint — irrelevant for XYZ/Vector)
    if (this.serviceType === 'WMS' || this.serviceType === 'WMTS') {
      if (!outerForm.valid) {
        const controls = outerForm.controls;
        for (const name in controls) {
          if (controls[name].invalid) {
            controls[name].markAsTouched();
            controls[name].markAsDirty();
          }
        }
        valid = false;
      }
    }

    // sub-form validation applies to all service types
    for (const form of this.forms.values()) {
      if (!form.valid) {
        for (const name in form.controls) {
          if (form.controls[name].invalid) {
            form.controls[name].markAsTouched();
            form.controls[name].markAsDirty();
          }
        }
        valid = false;
      }
    }

    return valid;
  }
}
